


import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Select, message } from "antd";
import {
    VideoCameraOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    SearchOutlined,
    FireOutlined,
    AppstoreOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import liveTrainingService from "../../../services/liveTraining.service";
import courseService from "../../../services/course.service";

const { Search } = Input;

// 🎨 COULEURS NOMIDYA
const C = {
    blue: "#0054A6",
    blueDark: "#003B75",
    blueDeep: "#002147",
    orange: "#E8860C",
    orangeLight: "#FF8C00",
    orangeDark: "#C46F0A",
};

const LiveTrainingsPublic = () => {
    const navigate = useNavigate();

    const [allTrainings, setAllTrainings] = useState([]);
    const [filteredTrainings, setFilteredTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // useEffect(() => {
    //     const fetchData = async () => {
    //         try {
    //             setLoading(true);
    //             const [trainings, cats] = await Promise.all([
    //                 liveTrainingService.getAllActive(),
    //                 courseService.getCategories(),
    //             ]);
    //             setAllTrainings(trainings || []);
    //             setCategories(cats || []);
    //         } catch (error) {
    //             console.error("Erreur chargement:", error);
    //             message.error("Erreur lors du chargement");
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchData();
    // }, []);
useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                let [trainings, cats] = await Promise.all([
                    liveTrainingService.getAllActive(),
                    courseService.getCategories(),
                ]);

                // 🔧 Si des miniatures manquent, on lance le backfill une fois puis on recharge
                const manqueThumbnails = (trainings || []).some((t) => !t.thumbnail);
                if (manqueThumbnails) {
                    try {
                        await liveTrainingService.backfillThumbnails();
                        trainings = await liveTrainingService.getAllActive();
                    } catch (e) {
                        console.warn("Backfill non effectué (droits admin requis) :", e?.response?.status);
                    }
                }

                setAllTrainings(trainings || []);
                setCategories(cats || []);
            } catch (error) {
                console.error("Erreur chargement:", error);
                message.error("Erreur lors du chargement");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    
    useEffect(() => {
        let result = [...allTrainings];

        if (selectedCategory) {
            result = result.filter((t) => t.categoryId === selectedCategory);
        }

        if (searchText) {
            const lower = searchText.toLowerCase();
            result = result.filter(
                (t) =>
                    t.title?.toLowerCase().includes(lower) ||
                    t.short_desc?.toLowerCase().includes(lower)
            );
        }

        setFilteredTrainings(result);
    }, [searchText, selectedCategory, allTrainings]);

    const handleViewDetails = (id) => {
        navigate(`/live-training/${id}`);
    };

    return (
        <div className="public-live-container">
            <style>{`
                .public-live-container {
                    min-height: 100vh;
                    background: #f8f9fa;
                }

                /* ===== HERO BLEU NOMIDYA ===== */
                .hero-section {
                    background: linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blueDark} 50%, ${C.blue} 100%);
                    padding: 80px 32px 100px;
                    color: white;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    background-image:
                        radial-gradient(circle at 20% 50%, rgba(232,134,12,0.15) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                    max-width: 800px;
                    margin: 0 auto;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    padding: 8px 20px;
                    border-radius: 30px;
                    font-size: 13px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 12px rgba(232,134,12,0.4);
                }

                .hero-badge::before {
                    content: '';
                    width: 8px;
                    height: 8px;
                    background: #fff;
                    border-radius: 50%;
                    animation: pulse 1.5s infinite;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.2); }
                }

                .hero-section h1 {
                    font-size: 48px;
                    font-weight: 800;
                    margin: 0 0 16px;
                    line-height: 1.1;
                    text-shadow: 0 2px 10px rgba(0,0,0,0.2);
                }

                .hero-section .highlight {
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-section .subtitle {
                    font-size: 18px;
                    opacity: 0.95;
                    max-width: 600px;
                    margin: 0 auto 32px;
                    line-height: 1.6;
                }

                .hero-stats {
                    display: flex;
                    justify-content: center;
                    gap: 40px;
                    flex-wrap: wrap;
                    margin-top: 40px;
                }

                .hero-stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .hero-stat-value {
                    font-size: 36px;
                    font-weight: 800;
                    line-height: 1;
                    color: ${C.orange};
                }

                .hero-stat-label {
                    font-size: 13px;
                    opacity: 0.9;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-top: 4px;
                }

                /* ===== FILTERS ===== */
                .filters-section {
                    background: white;
                    padding: 24px 32px;
                    box-shadow: 0 2px 12px rgba(0,84,166,0.08);
                    margin-top: -40px;
                    position: relative;
                    z-index: 10;
                    border-radius: 20px 20px 0 0;
                    max-width: 1300px;
                    margin-left: auto;
                    margin-right: auto;
                }

                .filters-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .filter-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: ${C.blueDark};
                    font-size: 15px;
                    font-weight: 600;
                }

                .filter-info .count-badge {
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 13px;
                    font-weight: 700;
                }

                /* ===== CARDS ===== */
                .cards-section {
                    max-width: 1300px;
                    margin: 0 auto;
                    padding: 40px 32px;
                }

                .cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 28px;
                }

                .training-card {
                    background: white;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 4px 16px rgba(0,84,166,0.08);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1px solid #f0f0f0;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                }

                .training-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 20px 40px rgba(0,84,166,0.15);
                    border-color: ${C.blue}33;
                }

                .training-card:hover .btn-view {
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white;
                    border-color: ${C.orange};
                }

                .card-image {
                    position: relative;
                    width: 100%;
                    height: 200px;
                    background: linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 100%);
                    overflow: hidden;
                }

                .card-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.4s;
                }

                .training-card:hover .card-image img {
                    transform: scale(1.08);
                }

                .card-image-placeholder {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: white;
                    font-size: 70px;
                    opacity: 0.6;
                }

                /* Badge LIVE - GARDÉ EN ROUGE */
                .live-badge {
                    position: absolute;
                    top: 16px;
                    left: 16px;
                    background: rgba(255, 0, 0, 0.95);
                    color: white;
                    padding: 6px 14px;
                    border-radius: 30px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                }

                .live-badge::before {
                    content: '';
                    width: 8px;
                    height: 8px;
                    background: white;
                    border-radius: 50%;
                    animation: pulse 1.5s infinite;
                }

                .price-tag {
                    position: absolute;
                    bottom: 16px;
                    right: 16px;
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white;
                    padding: 8px 18px;
                    border-radius: 30px;
                    font-weight: 800;
                    font-size: 16px;
                    box-shadow: 0 4px 12px rgba(232,134,12,0.4);
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .card-body {
                    padding: 24px;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }

                .card-category {
                    display: inline-block;
                    background: #e6f0ff;
                    color: ${C.blue};
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 12px;
                    width: fit-content;
                    border: 1px solid ${C.blue}22;
                }

                .card-title {
                    margin: 0 0 12px 0;
                    font-size: 19px;
                    font-weight: 700;
                    color: #1a1a1a;
                    line-height: 1.3;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .card-desc {
                    color: #6b7280;
                    font-size: 14px;
                    margin: 0 0 20px 0;
                    line-height: 1.6;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    flex: 1;
                }

                .card-meta {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    padding: 16px 0;
                    border-top: 1px solid #f0f0f0;
                    margin-bottom: 16px;
                }

                .card-meta-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #4b5563;
                    font-size: 13px;
                }

                .card-meta-row .anticon {
                    color: ${C.blue};
                    font-size: 16px;
                }

                .btn-view {
                    width: 100%;
                    padding: 12px;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    border: 2px solid ${C.blue};
                    background: white;
                    color: ${C.blue};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.3s;
                }

                .empty-state {
                    background: white;
                    border-radius: 20px;
                    padding: 80px 32px;
                    text-align: center;
                    box-shadow: 0 4px 16px rgba(0,84,166,0.05);
                }

                .empty-icon {
                    font-size: 72px;
                    color: ${C.blue};
                    opacity: 0.3;
                    margin-bottom: 24px;
                }

                .empty-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin: 0 0 8px;
                }

                .empty-desc {
                    color: #6b7280;
                    font-size: 15px;
                }

                @media (max-width: 768px) {
                    .hero-section { padding: 50px 20px 80px; }
                    .hero-section h1 { font-size: 32px; }
                    .hero-section .subtitle { font-size: 16px; }
                    .hero-stats { gap: 24px; }
                    .filters-section { padding: 20px; margin-top: -30px; border-radius: 16px 16px 0 0; }
                    .filters-content { flex-direction: column; align-items: stretch; }
                    .cards-section { padding: 24px 16px; }
                    .cards-grid { grid-template-columns: 1fr; }
                }
            `}</style>

            {/* HERO BLEU NOMIDYA */}
            <div className="hero-section">
                <div className="hero-content">
                    <div className="hero-badge">
                        <FireOutlined /> Formations Premium
                    </div>
                    <h1>Formations Live avec nos <span className="highlight">Experts</span></h1>
                    <p className="subtitle">
                        Découvrez nos formations premium animées par des formateurs passionnés
                        et progressez à votre rythme avec un contenu de qualité.
                    </p>

                    <div className="hero-stats">
                        <div className="hero-stat">
                            <div className="hero-stat-value">{allTrainings.length}</div>
                            <div className="hero-stat-label">Formations disponibles</div>
                        </div>
                        <div className="hero-stat">
                            <div className="hero-stat-value">{categories.length}</div>
                            <div className="hero-stat-label">Catégories</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FILTERS */}
            <div className="filters-section">
                <div className="filters-content">
                    <div className="filter-info">
                        <AppstoreOutlined />
                        <span>Toutes les formations</span>
                        <span className="count-badge">{filteredTrainings.length}</span>
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Select
                            placeholder="Toutes les catégories"
                            allowClear
                            style={{ width: 200 }}
                            value={selectedCategory}
                            onChange={(val) => setSelectedCategory(val)}
                            options={categories.map((c) => ({
                                value: c.id,
                                label: c.libelle,
                            }))}
                        />
                        <Search
                            placeholder="Rechercher..."
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<SearchOutlined />}
                            style={{ width: 240 }}
                        />
                    </div>
                </div>
            </div>

            {/* CARDS */}
            <div className="cards-section">
                {loading ? (
                    <div style={{
                        minHeight: '50vh',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 20px',
                    }}>
                        <style>{`
                            @keyframes nomidya-pulse {
                                0%, 100% { transform: scale(1); opacity: 1; }
                                50% { transform: scale(1.1); opacity: 0.7; }
                            }
                            @keyframes nomidya-rotate {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                            @keyframes nomidya-fade-in {
                                from { opacity: 0; transform: translateY(20px); }
                                to { opacity: 1; transform: translateY(0); }
                            }
                            @keyframes nomidya-bounce {
                                0%, 100% { transform: translateY(0); }
                                50% { transform: translateY(-12px); }
                            }
                            @keyframes nomidya-progress {
                                0% { width: 0%; margin-left: 0%; }
                                50% { width: 70%; margin-left: 15%; }
                                100% { width: 0%; margin-left: 100%; }
                            }

                            .nomidya-loader-card {
                                background: white;
                                border-radius: 24px;
                                padding: 60px 50px;
                                box-shadow: 0 25px 50px rgba(0, 84, 166, 0.12);
                                text-align: center;
                                max-width: 500px;
                                width: 100%;
                                animation: nomidya-fade-in 0.6s ease-out;
                                border: 1px solid rgba(0, 84, 166, 0.08);
                            }

                            .nomidya-loader-icon-wrapper {
                                position: relative;
                                width: 100px;
                                height: 100px;
                                margin: 0 auto 32px;
                            }

                            .nomidya-loader-ring {
                                position: absolute;
                                top: 0; left: 0;
                                width: 100px; height: 100px;
                                border-radius: 50%;
                                border: 4px solid transparent;
                                border-top-color: ${C.blue};
                                border-right-color: ${C.orange};
                                animation: nomidya-rotate 1.2s linear infinite;
                            }

                            .nomidya-loader-ring-2 {
                                position: absolute;
                                top: 10px; left: 10px;
                                width: 80px; height: 80px;
                                border-radius: 50%;
                                border: 3px solid transparent;
                                border-bottom-color: ${C.blueDark};
                                border-left-color: ${C.orangeLight};
                                animation: nomidya-rotate 1.5s linear infinite reverse;
                            }

                            .nomidya-loader-center {
                                position: absolute;
                                top: 50%; left: 50%;
                                transform: translate(-50%, -50%);
                                width: 50px; height: 50px;
                                background: linear-gradient(135deg, ${C.blue} 0%, ${C.orange} 100%);
                                border-radius: 12px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-size: 24px;
                                animation: nomidya-pulse 2s ease-in-out infinite;
                                box-shadow: 0 8px 24px rgba(0, 84, 166, 0.3);
                            }

                            .nomidya-loader-title {
                                font-size: 22px;
                                font-weight: 700;
                                color: ${C.blueDark};
                                margin: 0 0 8px;
                                letter-spacing: -0.3px;
                            }

                            .nomidya-loader-subtitle {
                                font-size: 14px;
                                color: #6b7280;
                                margin: 0 0 24px;
                                line-height: 1.6;
                            }

                            .nomidya-loader-dots {
                                display: flex;
                                justify-content: center;
                                gap: 10px;
                                margin-top: 20px;
                            }

                            .nomidya-loader-dot {
                                width: 10px;
                                height: 10px;
                                border-radius: 50%;
                                animation: nomidya-bounce 1.4s ease-in-out infinite;
                            }

                            .nomidya-loader-dot:nth-child(1) { background: ${C.blue}; animation-delay: 0s; }
                            .nomidya-loader-dot:nth-child(2) { background: ${C.orange}; animation-delay: 0.2s; }
                            .nomidya-loader-dot:nth-child(3) { background: ${C.blueDark}; animation-delay: 0.4s; }

                            .nomidya-loader-progress {
                                width: 100%;
                                height: 4px;
                                background: #e8eef5;
                                border-radius: 4px;
                                overflow: hidden;
                                margin-top: 24px;
                            }

                            .nomidya-loader-progress-bar {
                                height: 100%;
                                background: linear-gradient(90deg, ${C.blue} 0%, ${C.orange} 100%);
                                border-radius: 4px;
                                width: 30%;
                                animation: nomidya-progress 2s ease-in-out infinite;
                            }
                        `}</style>

                        <div className="nomidya-loader-card">
                            <div className="nomidya-loader-icon-wrapper">
                                <div className="nomidya-loader-ring"></div>
                                <div className="nomidya-loader-ring-2"></div>
                                <div className="nomidya-loader-center">
                                    <VideoCameraOutlined />
                                </div>
                            </div>

                            <h2 className="nomidya-loader-title">
                                Chargement des formations live
                            </h2>

                            <p className="nomidya-loader-subtitle">
                                Préparation du catalogue interactif...
                            </p>

                            <div className="nomidya-loader-dots">
                                <div className="nomidya-loader-dot"></div>
                                <div className="nomidya-loader-dot"></div>
                                <div className="nomidya-loader-dot"></div>
                            </div>

                            <div className="nomidya-loader-progress">
                                <div className="nomidya-loader-progress-bar"></div>
                            </div>
                        </div>
                    </div>
                ) : filteredTrainings.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">
                            <VideoCameraOutlined />
                        </div>
                        <h3 className="empty-title">
                            {searchText
                                ? "Aucun résultat pour cette recherche"
                                : "Aucune formation disponible pour le moment"}
                        </h3>
                        <p className="empty-desc">
                            Revenez bientôt pour découvrir nos prochaines formations !
                        </p>
                    </div>
                ) : (
                    <div className="cards-grid">
                        {filteredTrainings.map((training) => (
                            <div
                                key={training.id}
                                className="training-card"
                                onClick={() => handleViewDetails(training.id)}
                            >
                                <div className="card-image">
                                    {training.thumbnail ? (
                                        <img src={training.thumbnail} alt={training.title} loading="lazy" />
                                    ) : (
                                        <div className="card-image-placeholder">
                                            <VideoCameraOutlined />
                                        </div>
                                    )}
                                    <div className="live-badge">LIVE</div>
                                    <div className="price-tag">
                                        <DollarOutlined /> {training.price} €
                                    </div>
                                </div>

                                <div className="card-body">
                                    {training.categoryLibelle && (
                                        <span className="card-category">{training.categoryLibelle}</span>
                                    )}

                                    <h3 className="card-title">{training.title}</h3>

                                    <p className="card-desc">
                                        {training.short_desc || "Découvrez cette formation premium."}
                                    </p>

                                    <div className="card-meta">
                                        {training.duration && (
                                            <div className="card-meta-row">
                                                <ClockCircleOutlined />
                                                <span>Durée : {training.duration}</span>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        className="btn-view"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewDetails(training.id);
                                        }}
                                    >
                                        Voir les détails <ArrowRightOutlined />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveTrainingsPublic;

